import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { FormGroup, FormLabel, FormRow } from '../../components/FormField';

/**
 * The three "lateral panel" action modals from the HTML prototype
 * (POS Detail Drawer § Add note · My own contact · Upload winelist).
 * The fourth action, "Email", reuses the existing `WriteEmailModal`
 * from `CrmModals.tsx`.
 */

export type PosNoteVisibility = 'private' | 'shared';

export interface PosNote {
  id: string;
  text: string;
  visibility: PosNoteVisibility;
  when: string;
  by: string;
}

export interface PosOwnContact {
  name: string;
  role: string;
  email: string;
  phone: string;
  visibility: PosNoteVisibility;
}

export interface PosDocument {
  id: string;
  name: string;
  type: string;
  date: string;
}

const NoteIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const SharedIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const VisibilityRadios: React.FC<{
  name: string;
  value: PosNoteVisibility;
  onChange: (v: PosNoteVisibility) => void;
  privateHint: string;
  sharedHint: string;
}> = ({ name, value, onChange, privateHint, sharedHint }) => (
  <VisRow role="radiogroup">
    <VisOpt $checked={value === 'private'}>
      <input
        type="radio"
        name={name}
        checked={value === 'private'}
        onChange={() => onChange('private')}
      />
      <VisIcon>
        <NoteIcon />
      </VisIcon>
      <VisBody>
        <strong>Private</strong>
        <small>{privateHint}</small>
      </VisBody>
    </VisOpt>
    <VisOpt $checked={value === 'shared'}>
      <input
        type="radio"
        name={name}
        checked={value === 'shared'}
        onChange={() => onChange('shared')}
      />
      <VisIcon>
        <SharedIcon />
      </VisIcon>
      <VisBody>
        <strong>Shared with my team</strong>
        <small>{sharedHint}</small>
      </VisBody>
    </VisOpt>
  </VisRow>
);

/* ─── Add Note Modal ─── */

interface AddNoteModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (text: string, visibility: PosNoteVisibility) => void;
}

export const AddNoteModal: React.FC<AddNoteModalProps> = ({
  open,
  onClose,
  onSave
}) => {
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState<PosNoteVisibility>('private');

  useEffect(() => {
    if (open) {
      setText('');
      setVisibility('private');
    }
  }, [open]);

  const handleSave = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSave(trimmed, visibility);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a note"
      subtitle="Capture context, follow-ups or insights about this POS."
      maxWidth="560px"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!text.trim()}
          >
            Save note
          </Button>
        </>
      }
    >
      <FormGroup>
        <FormLabel htmlFor="posNoteText">Note</FormLabel>
        <Textarea
          id="posNoteText"
          rows={6}
          placeholder="e.g. Spoke with Bjørn after service — he's interested in a 2017 vertical for next quarter…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </FormGroup>
      <FormGroup>
        <FormLabel>Visibility</FormLabel>
        <VisibilityRadios
          name="posNoteVis"
          value={visibility}
          onChange={setVisibility}
          privateHint="Only you can see this note."
          sharedHint="Everyone in your account can read this note."
        />
      </FormGroup>
    </Modal>
  );
};

/* ─── My Own Contact Modal ─── */

interface OwnContactModalProps {
  open: boolean;
  onClose: () => void;
  initial: PosOwnContact | null;
  onSave: (contact: PosOwnContact) => void;
}

const EMPTY_CONTACT: PosOwnContact = {
  name: '',
  role: '',
  email: '',
  phone: '',
  visibility: 'private'
};

export const OwnContactModal: React.FC<OwnContactModalProps> = ({
  open,
  onClose,
  initial,
  onSave
}) => {
  const [form, setForm] = useState<PosOwnContact>(EMPTY_CONTACT);

  useEffect(() => {
    if (open) setForm(initial ?? EMPTY_CONTACT);
  }, [open, initial]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    onSave(form);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add your own contact"
      subtitle="Store a personal contact at this POS — sommelier, manager, owner — alongside the WS-tracked one."
      maxWidth="580px"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!form.name.trim()}
          >
            Save contact
          </Button>
        </>
      }
    >
      <FormGroup>
        <FormLabel htmlFor="posContactName" required>
          Full name
        </FormLabel>
        <PlainInput
          id="posContactName"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="e.g. Bjørn Tangvald"
        />
      </FormGroup>
      <FormGroup>
        <FormLabel htmlFor="posContactRole">Role — optional</FormLabel>
        <PlainInput
          id="posContactRole"
          name="role"
          value={form.role}
          onChange={handleChange}
          placeholder="e.g. Head sommelier"
        />
      </FormGroup>
      <FormRow>
        <FormGroup>
          <FormLabel htmlFor="posContactEmail">Email</FormLabel>
          <PlainInput
            id="posContactEmail"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="bjorn@lysverket.no"
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="posContactPhone">Phone</FormLabel>
          <PlainInput
            id="posContactPhone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            placeholder="+47 41 478 526"
          />
        </FormGroup>
      </FormRow>
      <FormGroup>
        <FormLabel>Visibility</FormLabel>
        <VisibilityRadios
          name="posContactVis"
          value={form.visibility}
          onChange={(v) => setForm((prev) => ({ ...prev, visibility: v }))}
          privateHint="Only visible to you."
          sharedHint="Visible to everyone in your account."
        />
      </FormGroup>
    </Modal>
  );
};

/* ─── Upload Winelist Modal ─── */

interface UploadWinelistModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (doc: PosDocument, file: File | null) => void;
}

const DOC_TYPE_OPTIONS = [
  { value: 'Wine List · PDF', label: 'Wine list' },
  { value: 'Drinks List · PDF', label: 'Drinks list' },
  { value: 'By-the-glass · PDF', label: 'By-the-glass selection' },
  { value: 'Cocktail List · PDF', label: 'Cocktail list' },
  { value: 'Other · PDF', label: 'Other' }
];

export const UploadWinelistModal: React.FC<UploadWinelistModalProps> = ({
  open,
  onClose,
  onUpload
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [date, setDate] = useState('');
  const [docType, setDocType] = useState(DOC_TYPE_OPTIONS[0].value);

  useEffect(() => {
    if (open) {
      setFile(null);
      setDate(new Date().toISOString().slice(0, 10));
      setDocType(DOC_TYPE_OPTIONS[0].value);
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleUpload = () => {
    if (!file) return;
    onUpload(
      {
        id: `${Date.now()}`,
        name: file.name,
        type: docType,
        date
      },
      file
    );
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload winelist"
      subtitle="Attach a PDF, image or scan of the current beverage list. We'll add it to this POS so you and your team can review it later."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!file}
            onClick={handleUpload}
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            }
          >
            Upload winelist
          </Button>
        </>
      }
    >
      <FormGroup>
        <FormLabel>Document</FormLabel>
        <DropZone as="label" htmlFor="posUploadFileInput">
          <DropIcon
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </DropIcon>
          <DropText>
            <strong>Choose a file</strong>
            <span>or drag and drop</span>
          </DropText>
          <DropHint>PDF, PNG, JPG, DOCX, XLSX — up to 25 MB</DropHint>
          <input
            type="file"
            id="posUploadFileInput"
            hidden
            accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx"
            onChange={handleFileChange}
          />
        </DropZone>
        {file && (
          <FileChip>
            {file.name}
            <FileChipSize>{formatBytes(file.size)}</FileChipSize>
          </FileChip>
        )}
      </FormGroup>
      <FormRow>
        <FormGroup>
          <FormLabel htmlFor="posUploadDate">Collected on</FormLabel>
          <PlainInput
            id="posUploadDate"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="posUploadType">Document type</FormLabel>
          <PlainSelect
            id="posUploadType"
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
          >
            {DOC_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </PlainSelect>
        </FormGroup>
      </FormRow>
    </Modal>
  );
};

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/* ─── Styled ─── */

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 8px;
  font-size: 13.5px;
  font-family: var(--font-sans);
  resize: vertical;
  color: var(--ink);
  background: var(--white);

  &:focus {
    outline: none;
    border-color: var(--terracotta);
  }
`;

const PlainInput = styled.input`
  padding: 9px 12px;
  border: 1px solid var(--line);
  border-radius: 7px;
  font-size: 14px;
  font-family: var(--font-sans);
  background: var(--white);
  color: var(--ink);

  &:focus {
    outline: none;
    border-color: var(--terracotta);
  }

  &::placeholder {
    color: var(--ink-faint);
  }
`;

const PlainSelect = styled.select`
  padding: 9px 12px;
  border: 1px solid var(--line);
  border-radius: 7px;
  font-size: 14px;
  font-family: var(--font-sans);
  background: var(--white);
  color: var(--ink);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--terracotta);
  }
`;

const VisRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const VisOpt = styled.label<{ $checked: boolean }>`
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid
    ${({ $checked }) => ($checked ? 'var(--terracotta)' : 'var(--line)')};
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $checked }) =>
    $checked ? 'rgba(200, 75, 31, 0.06)' : 'var(--white)'};

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

const VisIcon = styled.span`
  color: var(--ink-faint);
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const VisBody = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;

  strong {
    font-size: 13px;
    color: var(--ink);
  }

  small {
    color: var(--ink-muted);
  }
`;

const DropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 24px 16px;
  border: 1.5px dashed var(--line);
  border-radius: 10px;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--terracotta);
  }
`;

const DropIcon = styled.svg`
  width: 26px;
  height: 26px;
  color: var(--terracotta);
`;

const DropText = styled.div`
  font-size: 13px;
  color: var(--ink);

  span {
    color: var(--ink-muted);
  }
`;

const DropHint = styled.div`
  font-size: 11px;
  color: var(--ink-faint);
`;

const FileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 6px 10px;
  border-radius: 6px;
  background: var(--paper, #f8f7f5);
  font-size: 12px;
  color: var(--ink);
`;

const FileChipSize = styled.span`
  color: var(--ink-faint);
`;
